import { useState, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import {
  OverrideType,
  PhoneOverride,
  CallRouteType_Enum,
  ListResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import dayjs from 'dayjs';
import { ForwardingNumberApi } from '@frontend/api-forwarding-number';
import { LocationsApi } from '@frontend/api-locations';
import PhoneTreeAPI from '@frontend/api-phone-trees';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  useModalControl,
  UseFormReturnType,
  useForm,
  Tray,
  Heading,
  CheckboxField,
  DatePickerField,
  TimeField,
  SecondaryButton,
  PrimaryButton,
  Text,
  useAlert,
  TextLink,
  DropdownField,
  Button,
  RadioCardField,
  Modal,
  Truncated,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { useOverridesManagementPermission } from '../../userManagementPermission';
import { buildAudioLibraryPath } from '../../utils/media-path';
import { defaultMediaID, getNameWithoutExtension } from '../../utils/phone-utils';
import { AudioQueryMedia, useAudioQuery } from '../../utils/use-audio-query';
import { newOverrideButtonSectionStyle, overrideSettingsStyle } from '../../views/settings/overrides-styles';
import { AudioPicker, DEFAULT_GREETING_PATH, findMediaItem } from '../audio-picker/audio-picker';
import {
  PhoneTreeDropdown,
  PhoneTreePreviewModal,
} from '../call-routes/call-route-details/add-step-panel/phone-tree-duplicate-step/phone-tree-dropdown';
import { useCallRouteGraphsShallowStore } from '../call-routes/call-route-details/call-route-store';
import { getCanvasPhoneTrees } from '../call-routes/helpers/graph/node-utils';
import { CachedAudioScrubber } from '../common/cached-audio-scrubber';
import { getMediaValues } from '../department/phone-routing-settings';
import { ForwardingNumberPicker } from '../forwarding-number-picker';
import { TraySubHeader } from '../tray-sub-header';
import useVoicemailBoxes from '../voicemail-box/new/use-voicemail-boxes';
import { NewOverrideSettingRemoveModal } from './override-settings-remove-modal.new';
import { OverrideCreatePayload, OverrideUpdatePayload, PhoneOverrideType, SchemaFormValues } from './types';
import { useOverrideMutations } from './use-overrides-mutations';

const formConfigBase = {
  voicemailBoxId: { type: 'dropdown' },
  voicemailMediaId: { type: 'dropdown' },
  phoneTreeId: { type: 'dropdown' },
  phoneTreeInfoMediaId: { type: 'dropdown' },
  forwardingNumberId: { type: 'dropdown' },
  selectOverride: { type: 'radio' },
  addSchedule: { type: 'checkbox' },
  startDate: { type: 'datePicker' },
  startTime: { type: 'time' },
  endDate: { type: 'datePicker' },
  endTime: { type: 'time' },
} as const;

interface IOverrideSettingTray {
  mountTarget?: string;
  override?: PhoneOverrideType | PhoneOverride;
  locationId?: string;
  isCallRouteFlow?: boolean;
  modalControls: ReturnType<typeof useModalControl>;
  overridesData?: ListResponse;
}

// We need to calculate the time difference between userTime and officeTime. This difference will be applied to the payload to ensure that the time in the timeField aligns with the office timezone, ensuring consistency between the selected time and the expected response.
const getTimezoneDifference = (officeTimezone: string | undefined) => {
  const userTime = new Date(dayjs().format('MM/DD/YY HH:mm'));
  const officeTime = new Date(dayjs().tz(officeTimezone).format('MM/DD/YY HH:mm'));
  return (officeTime.getTime() - userTime.getTime()) / (1000 * 60 * 60);
};

const timeToSeconds = (timeStr: string) => {
  const [hours, minutes, seconds] = timeStr.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const NewOverrideSettingTray = ({
  mountTarget,
  override,
  locationId: iLID,
  modalControls,
  isCallRouteFlow,
  overridesData,
}: IOverrideSettingTray) => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });
  const alerts = useAlert();
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const locationData = accessibleLocationData[selectedLocationIds[0]];
  const phoneTenantId = locationData?.phoneTenantId ?? '';
  const instructionSetId = override?.instructionSetId ?? '';
  const { data: audioFiles } = useAudioQuery({
    tenantId: phoneTenantId,
    allowedOptions: { standard: true, custom: true, mailbox: true },
    mailboxId: override?.voicemailBoxId,
  });

  const { getFeatureFlagValue } = useAppFlagStore();
  const hasPhoneTreeOverridesFlag = getFeatureFlagValue({
    flagName: 'pc-phone-tree-overrides',
    locationIds: selectedLocationIds,
  });

  const getMediaNameById = (id: string) => {
    const media = audioFiles?.allMedia.find((item) => item.id === id);
    return media ? getNameWithoutExtension(media.name) : null;
  };

  const isDeviceRouteOverride = override?.callRouteType === CallRouteType_Enum.DEVICE_EXTENSION;
  const callRouteId = override?.callRouteId;

  const { data: forwardingNumbers } = useQuery({
    queryKey: [override?.locationId, ...queryKeys.forwardingNumbers()],
    queryFn: () => ForwardingNumberApi.list({ locationId: override?.locationId }),
    enabled: !!override,
  });
  const forwardingNumber = forwardingNumbers?.find((item) => item.id === override?.forwardingNumberId);

  const lastUsedVMO = overridesData?.lastUsed?.[instructionSetId ?? '']?.lastUsed?.[OverrideType.OVERRIDE_TYPE_VMO];
  const lastUsedFWD = overridesData?.lastUsed?.[instructionSetId ?? '']?.lastUsed?.[OverrideType.OVERRIDE_TYPE_FWD];
  const lastUsedIVR = overridesData?.lastUsed?.[instructionSetId ?? '']?.lastUsed?.[OverrideType.OVERRIDE_TYPE_IVR];
  const lastUsedMediaItemId = override?.mediaItemId ? '' : lastUsedVMO?.mediaItemId;
  const lastUsedForwardingNumberId = override?.forwardingNumberId ? '' : lastUsedFWD?.forwardingNumberId;
  const lastUsedVoicemailBoxId = override?.voicemailBoxId ? '' : lastUsedVMO?.voicemailBoxId;
  const lastUsedIVRId = override?.ivrMenuId ? '' : lastUsedIVR?.ivrMenuId;
  const lastUsedIVRMediaId = override?.mediaItemId ? '' : lastUsedIVR?.mediaItemId;

  const {
    getTempEditingNodesByCallRouteId,
    getTempEditingEdgesByCallRouteId,
    getNodesByCallRouteId,
    getEdgesByCallRouteId,
  } = useCallRouteGraphsShallowStore(
    'getTempEditingNodesByCallRouteId',
    'getTempEditingEdgesByCallRouteId',
    'getNodesByCallRouteId',
    'getEdgesByCallRouteId'
  );

  const canvasNodes =
    getTempEditingNodesByCallRouteId(callRouteId as string) || getNodesByCallRouteId(callRouteId as string) || [];
  const canvasEdges =
    getTempEditingEdgesByCallRouteId(callRouteId as string) || getEdgesByCallRouteId(callRouteId as string) || [];

  const phoneTreesInCallRoute = callRouteId ? getCanvasPhoneTrees(canvasNodes, canvasEdges) : [];

  const getVoicemailBoxNameById = (id: string) => {
    const voicemailBox = voicemailBoxes?.find((item) => item.mailbox.id === id);
    return voicemailBox ? voicemailBox.mailbox.name : null;
  };

  const hasUserManagementPermission = useOverridesManagementPermission();
  const removeOverride = useModalControl();
  const { modalProps: previewPhoneTreeModalProps, triggerProps: previewPhoneTreeTriggerProps } = useModalControl();
  const locationId = iLID ?? override?.locationId ?? '';

  const { globalAvailableLocationIds } = usePhoneSettingsShallowStore('globalAvailableLocationIds');

  const { data: voicemailBoxes = [] } = useVoicemailBoxes({
    phoneTenantId: locationData.phoneTenantId ?? '',
  });

  const filteredVoicemailBoxes = useMemo(
    () =>
      scopedFilterAndSort({
        data: voicemailBoxes,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: [],
        shouldSort: false,
      }),
    [voicemailBoxes, globalAvailableLocationIds]
  );

  const voicemailBox = filteredVoicemailBoxes.filter((vm) => vm.mailbox.id === override?.voicemailBoxId);

  const { data: officeTimezone } = useQuery({
    queryKey: [locationId, ...queryKeys.timezone(locationId ?? '')],
    queryFn: () =>
      LocationsApi.getOfficeTimezone(
        {},
        {
          locationId,
        }
      ),
    select: (data) => data.timezone,
  });

  const currentDate = dayjs();
  const currentDay = dayjs().startOf('day');
  const currentDateFormatted = dayjs().format('MM/DD/YYYY');
  const roundedTime = currentDate.minute(Math.ceil(currentDate.minute() / 15) * 15);

  /// we know that if there is a matching override, then the override is being edited
  const isEdit = !!override?.id;

  const timezoneDiff = getTimezoneDifference(officeTimezone);
  const currentTimeOffice = roundedTime.add(timezoneDiff, 'hour').format('HH:mm');

  const createPostPayloadV2 = (
    values: UseFormReturnType<typeof formConfigBase, keyof typeof formConfigBase>['values'] & { id?: string },
    instructionSetId: string
  ): OverrideCreatePayload => {
    const formattedStartDateTime = dayjs(`${values.startDate} ${values.startTime}`)
      .subtract(timezoneDiff, 'hour')
      .format();
    const formattedEndDateTime = dayjs(`${values.endDate} ${values.endTime}`).subtract(timezoneDiff, 'hour').format();

    const baseObj: OverrideCreatePayload = {
      instructionSetId,
      overrideType: values.selectOverride as OverrideType,
    };

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailBoxId) {
      baseObj.voicemailBoxId = values.voicemailBoxId;
    } else baseObj.voicemailBoxId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailMediaId !== '') {
      baseObj.mediaItemId = values.voicemailMediaId && getMediaValues(values.voicemailMediaId);
    } else baseObj.mediaItemId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_FWD && values.forwardingNumberId !== '') {
      baseObj.forwardingNumberId = values.forwardingNumberId;
    } else baseObj.forwardingNumberId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR && values.phoneTreeId !== '') {
      baseObj.ivrMenuId = values.phoneTreeId;
    } else baseObj.ivrMenuId = '';

    if (
      baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR &&
      values.phoneTreeInfoMediaId !== '' &&
      values.phoneTreeInfoMediaId !== 'none'
    ) {
      baseObj.mediaItemId = values.phoneTreeInfoMediaId;
    } else baseObj.mediaItemId = '';

    if (values.addSchedule && values.startDate !== '' && values.startTime !== '') {
      baseObj.startAt = formattedStartDateTime;
    }

    if (values.addSchedule && values.endDate !== '' && values.endTime !== '') {
      baseObj.endAt = formattedEndDateTime;
    }

    return baseObj;
  };

  const updatePostPayloadV2 = (
    values: UseFormReturnType<typeof formConfigBase, keyof typeof formConfigBase>['values'] & { id?: string },
    instructionSetId: string
  ): OverrideUpdatePayload => {
    const formattedStartDateTime = dayjs(`${values.startDate} ${values.startTime}`)
      .subtract(timezoneDiff, 'hour')
      .format();
    const formattedEndDateTime = dayjs(`${values.endDate} ${values.endTime}`).subtract(timezoneDiff, 'hour').format();

    const baseObj: OverrideUpdatePayload = {
      instructionSetId,
      id: values.id || '',
      overrideType: values.selectOverride as OverrideType,
    };

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailBoxId) {
      baseObj.voicemailBoxId = values.voicemailBoxId;
    } else baseObj.voicemailBoxId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailMediaId) {
      baseObj.mediaItemId = getMediaValues(values.voicemailMediaId);
    } else baseObj.mediaItemId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_FWD && values.forwardingNumberId) {
      baseObj.forwardingNumberId = values.forwardingNumberId;
    } else baseObj.forwardingNumberId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR && values.phoneTreeId) {
      baseObj.ivrMenuId = values.phoneTreeId;
    } else baseObj.ivrMenuId = '';

    if (
      baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR &&
      values.phoneTreeInfoMediaId &&
      values.phoneTreeInfoMediaId !== 'none'
    ) {
      baseObj.mediaItemId = values.phoneTreeInfoMediaId;
    } else baseObj.mediaItemId = '';

    if (optionalFieldProps.value && values.addSchedule && values.startDate && values.startTime) {
      baseObj.startAt = formattedStartDateTime;
    }

    if (optionalFieldProps.value && values.addSchedule && values.endDate && values.endTime) {
      baseObj.endAt = formattedEndDateTime;
    }

    if (override?.id) {
      baseObj.id = override.id;
    }

    return baseObj;
  };

  const { formProps, getFieldProps, seedValues, validate } = useForm({
    computeChangedValues: true,
    fields: {
      voicemailBoxId: {
        type: 'dropdown',
        value: override?.voicemailBoxId,
      },
      voicemailMediaId: {
        type: 'dropdown',
        value:
          override?.overrideType === OverrideType.OVERRIDE_TYPE_VMO && override?.mediaItemId
            ? override?.mediaItemId
            : '',
      },
      phoneTreeId: { type: 'dropdown', value: override?.ivrMenuId },
      phoneTreeInfoMediaId: {
        type: 'dropdown',
        value:
          override?.overrideType === OverrideType.OVERRIDE_TYPE_IVR && override?.mediaItemId
            ? override?.mediaItemId
            : '',
      },
      forwardingNumberId: { type: 'dropdown' },
      selectOverride: { type: 'radio' },
      addSchedule: { type: 'checkbox' },
      startDate: {
        type: 'datePicker',
        validator: ({ value }, formValues) => {
          const startDate = dayjs(value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');
          // Skip start date validation for an active override (indicated by override?.enabled) if the start date and time have passed. This specifically applies when editing an override.
          if (formValues.endDate.value === '' && !override?.enabled) {
            return startDate.isBefore(currentDate, 'day') ? t('Start date must be in the future') : '';
          }

          return startDate.isAfter(endDate) ? t('Start date must be before the end date') : '';
        },
      },
      startTime: {
        type: 'time',
        validator: ({ value }, formValues) => {
          const startTime = timeToSeconds(value);
          const endTime = timeToSeconds(formValues.endTime.value);
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');
          const currentTime = timeToSeconds(dayjs().add(timezoneDiff, 'hour').format('HH:mm:ss'));
          // Skip start time validation for an active override (indicated by override?.enabled) if the start date and time have passed. This specifically applies when editing an override.
          if (startDate.isSame(currentDate, 'day') && !startDate.isSame(endDate) && !override?.enabled) {
            return startTime < currentTime ? t('Start time must be in the future') : '';
          }

          return startDate.isSame(endDate) && startTime >= endTime ? t('Start time must be before the end time') : '';
        },
      },
      endDate: {
        type: 'datePicker',
        validator: ({ value }, formValues) => {
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(value, 'MM/DD/YYYY').startOf('day');

          if (!formValues.startDate.value) {
            return endDate.isBefore(currentDay) ? t('End date must be in the future') : '';
          }

          return endDate.isBefore(startDate) ? t('End date must be after the start date') : '';
        },
      },
      endTime: {
        type: 'time',
        validator: ({ value }, formValues) => {
          const startTime = timeToSeconds(formValues.startTime.value);
          const endTime = timeToSeconds(value);
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');
          const currentTime = timeToSeconds(dayjs().add(timezoneDiff, 'hour').format('HH:mm:ss'));

          if (endDate.isSame(currentDate, 'day') && !startDate.isSame(endDate)) {
            return endTime < currentTime ? t('End time must be in the future') : '';
          }
          return startDate.isSame(endDate) && startTime >= endTime ? t('End time must be after the start time') : '';
        },
      },
    },
    onSubmit: (formValues) => {
      let instructionSetId = '';
      if (override?.instructionSetId) {
        instructionSetId = override.instructionSetId;
      } else if (override && 'instructionSetId' in override) {
        instructionSetId = override.instructionSetId;
      }
      if (override?.id) {
        return updateOverrideV2(formValues, instructionSetId);
      }
      return createOverrideV2(formValues, instructionSetId);
    },
  });

  const voicemailBoxProps = getFieldProps('voicemailBoxId');
  const vmOverrideMediaProps = getFieldProps('voicemailMediaId');
  const phoneTreeIdProps = getFieldProps('phoneTreeId');
  const phoneTreeInfoMediaIdProps = getFieldProps('phoneTreeInfoMediaId');
  const callForwardingNumberProps = getFieldProps('forwardingNumberId');
  const overrideSelectProps = getFieldProps('selectOverride');
  const optionalFieldProps = getFieldProps('addSchedule');
  const startDateFieldProps = getFieldProps('startDate');
  const startTimeFieldProps = getFieldProps('startTime');
  const endDateFieldProps = getFieldProps('endDate');
  const endTimeFieldProps = getFieldProps('endTime');
  const startDate = startDateFieldProps?.value;
  const endDate = endDateFieldProps?.value;
  const isStartToday = dayjs(startDate).isSame(currentDate, 'day');
  const isEndToday = dayjs(endDate).isSame(currentDate, 'day');

  const { data: phoneTreeData } = useQuery({
    queryKey: [locationData.phoneTenantId, ...queryKeys.settings.readPhoneTree(phoneTreeIdProps?.value ?? '')],
    queryFn: () => PhoneTreeAPI.Read({ phoneTreeId: phoneTreeIdProps?.value ?? '' }),
    enabled: !!locationData.phoneTenantId && !!phoneTreeIdProps?.value,
  });

  useEffect(() => {
    validate();
  }, [startDateFieldProps.value, endDateFieldProps.value, startTimeFieldProps.value, endTimeFieldProps.value]);

  useEffect(() => {
    if (!!override && timezoneDiff !== undefined) {
      seedValues({
        selectOverride:
          override.overrideType !== OverrideType.OVERRIDE_TYPE_UNSPECIFIED
            ? override.overrideType
            : OverrideType.OVERRIDE_TYPE_VMO,
        voicemailBoxId: override.voicemailBoxId || '',
        voicemailMediaId:
          override.overrideType === OverrideType.OVERRIDE_TYPE_VMO
            ? override.mediaItemId || (override.id ? 'default' : '')
            : '',
        phoneTreeId: override.ivrMenuId || '',
        phoneTreeInfoMediaId:
          override.overrideType === OverrideType.OVERRIDE_TYPE_IVR ? override.mediaItemId || 'none' : '',
        forwardingNumberId: override.forwardingNumberId,
        addSchedule: !!override.startAt || !!override.endAt,
        startDate: !!override.startAt ? dayjs(override.startAt).format('MM/DD/YYYY') : '',
        startTime: !!override.startAt ? dayjs(override.startAt).add(timezoneDiff, 'hour').format('h:mma') : '',
        endDate: !!override.endAt ? dayjs(override.endAt).format('MM/DD/YYYY') : '',
        endTime: !!override.endAt ? dayjs(override.endAt).add(timezoneDiff, 'hour').format('h:mma') : '',
      });
    } else
      seedValues({
        selectOverride: OverrideType.OVERRIDE_TYPE_VMO,
        voicemailBoxId: '',
        voicemailMediaId: '',
        forwardingNumberId: '',
        addSchedule: false,
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
      });
  }, [override, timezoneDiff, filteredVoicemailBoxes]);

  const hasValidationError =
    !!startDateFieldProps.error.length ||
    !!startTimeFieldProps.error.length ||
    !!endDateFieldProps.error.length ||
    !!endTimeFieldProps.error.length;

  const canUpdateOverride =
    (overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO &&
      !hasValidationError &&
      !!voicemailBoxProps.value &&
      !!vmOverrideMediaProps.value) ||
    (overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_FWD &&
      !hasValidationError &&
      !!callForwardingNumberProps.value) ||
    (overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_IVR &&
      !hasValidationError &&
      hasPhoneTreeOverridesFlag &&
      !!phoneTreeIdProps.value);

  const needTimeField =
    !!optionalFieldProps.value &&
    [
      { start: startDateFieldProps, end: startTimeFieldProps },
      { start: endDateFieldProps, end: endTimeFieldProps },
    ].some(({ start, end }) => !!start.value !== !!end.value);

  const handleCancel = () => {
    isCancelling.current = true;
    const isOverrideActive = override && !!override.id;
    const isVMO = isOverrideActive && override?.overrideType === OverrideType.OVERRIDE_TYPE_VMO;
    const isIVR = isOverrideActive && override?.overrideType === OverrideType.OVERRIDE_TYPE_IVR;
    seedValues({
      selectOverride: isOverrideActive ? override.overrideType : OverrideType.OVERRIDE_TYPE_VMO,
      voicemailBoxId: isOverrideActive ? override.voicemailBoxId : '',
      voicemailMediaId: isOverrideActive
        ? isVMO
          ? override.mediaItemId || 'default'
          : getMediaValues(defaultMediaID)
        : '',
      forwardingNumberId: isOverrideActive ? override.forwardingNumberId : '',
      phoneTreeId: isOverrideActive ? override.ivrMenuId : '',
      phoneTreeInfoMediaId: isOverrideActive ? (isIVR ? override.mediaItemId || 'none' : '') : '',
      addSchedule: isOverrideActive ? !!override.startAt || !!override.endAt : false,
      startDate: isOverrideActive && override.startAt ? dayjs(override.startAt).format('MM/DD/YYYY') : '',
      startTime: isOverrideActive && override.startAt ? dayjs(override.startAt).format('h:mma') : '',
      endDate: isOverrideActive && override.endAt ? dayjs(override.endAt).format('MM/DD/YYYY') : '',
      endTime: isOverrideActive && override.endAt ? dayjs(override.endAt).format('h:mma') : '',
    });

    // Reset flag after a short delay to allow the update to complete
    setTimeout(() => {
      isCancelling.current = false;
    }, 500);
  };

  const isCancelling = useRef(false);
  const previousVoicemailBoxId = useRef(voicemailBoxProps.value);

  // This is to reset voicmail greeting when voicemail box changes
  useEffect(() => {
    if (isCancelling.current) return;

    if (previousVoicemailBoxId.current && previousVoicemailBoxId.current !== voicemailBoxProps.value) {
      seedValues({
        voicemailMediaId: '',
      });
    }
    previousVoicemailBoxId.current = voicemailBoxProps.value;
  }, [voicemailBoxProps.value]);

  const renderButtons = () => (
    <>
      <SecondaryButton
        size='large'
        css={{ width: 'auto' }}
        onClick={() => {
          modalControls.closeModal();
          setTimeout(() => {
            // setTimeout for form to reset after closing the modal
            handleCancel();
          }, 300);
        }}
        trackingId='phone-portal-button-cancelOverride'
      >
        {t('Cancel')}
      </SecondaryButton>
      <PrimaryButton
        size='large'
        css={{ width: 'auto' }}
        disabled={!canUpdateOverride || needTimeField || !modalControls.modalProps.show}
        onClick={() => modalControls.closeModal()}
        type='submit'
        trackingId={
          isDeviceRouteOverride
            ? 'overrides-deviceExtensionRoute-createEdit-save-btn'
            : 'phone-portal-button-createEditOverride'
        }
      >
        {t('Save')}
      </PrimaryButton>
    </>
  );

  const { create, update } = useOverrideMutations(phoneTenantId);

  const createOverrideV2 = (formValues: SchemaFormValues, instructionSetId: string) => {
    const finalState = createPostPayloadV2(formValues, instructionSetId);
    create.mutate(finalState, {
      onSuccess: () => {
        alerts.success(t('Override created successfully.'));
      },
      onError: () => {
        alerts.error(t('Failed to create Override.'));
      },
    });
  };

  const updateOverrideV2 = (formValues: SchemaFormValues, instructionSetId: string) => {
    const finalState = updatePostPayloadV2(formValues, instructionSetId);
    update.mutate(finalState, {
      onSuccess: () => {
        alerts.success(t('Override updated successfully.'));
      },
      onError: () => {
        alerts.error(t('Failed to update Override.'));
      },
    });
  };

  const [overrideV2, setOverrideV2] = useState<PhoneOverrideType>();

  const handleOpenRemoveModalV2 = (override: PhoneOverrideType) => {
    setOverrideV2(override);
    removeOverride.openModal();
  };

  const media = findMediaItem(vmOverrideMediaProps.value, audioFiles?.allMedia ?? []);
  const resolvedPath = media ? buildAudioLibraryPath({ media }) : '';

  const scheduledCallRoute = [
    {
      id: override?.callRouteId ?? '',
      name: override?.callRouteName ?? '',
    },
  ];

  const overrideOptions = useMemo(
    () =>
      [
        {
          value: OverrideType.OVERRIDE_TYPE_VMO,
          title: t('Send to Voicemail'),
          description: t('All calls are automatically sent to voicemail'),
          trackingId: 'phone-portal-radio-vmo',
          show: true,
        },
        {
          value: OverrideType.OVERRIDE_TYPE_FWD,
          title: t('Forward Call'),
          description: t('All calls are forwarded to a predetermined phone number'),
          trackingId: 'phone-portal-radio-fwd',
          show: true,
        },
        {
          value: OverrideType.OVERRIDE_TYPE_IVR,
          title: t('Send to Phone Tree'),
          description: t('All calls are sent to a Phone Tree'),
          trackingId: 'phone-portal-radio-ivr',
          show: hasPhoneTreeOverridesFlag,
        },
      ].filter((option) => option.show),
    [t, hasPhoneTreeOverridesFlag]
  );

  return (
    <>
      <Tray
        mountTarget={mountTarget}
        width='xlarge'
        css={{ padding: theme.spacing(4), borderTop: `1px solid ${theme.colors.neutral20}`, overflowY: 'auto' }}
        {...modalControls.modalProps}
      >
        <form {...formProps}>
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: `calc(100vh - ${mountTarget ? theme.spacing(20) : theme.spacing(8)})`,
            }}
          >
            {!hasUserManagementPermission(override?.locationId ?? '') ? (
              <div>
                <Tray.Header
                  Buttons={
                    <Button
                      iconName='x'
                      variant='secondary'
                      size='large'
                      label='close'
                      onClick={() => {
                        modalControls.closeModal();
                        // setTimeout for form to reset after closing the modal
                        setTimeout(() => {
                          handleCancel();
                        }, 300);
                      }}
                    />
                  }
                >
                  <Heading level={2} css={{ marginBottom: 0 }}>
                    {t('View Override')}
                  </Heading>
                </Tray.Header>
                {override?.callRouteName && !isCallRouteFlow && (
                  <TraySubHeader
                    scheduleCallRoutes={scheduledCallRoute}
                    selectedLocationId={override?.locationId ?? ''}
                  />
                )}
                <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
                  {t('Override Type')}
                </Heading>
                <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                  {['OVERRIDE_TYPE_VMO', 'OVERRIDE_TYPE_FWD'].includes(overrideSelectProps.value) && (
                    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                      <Text weight='bold'>
                        {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO
                          ? t('Send to Voicemail')
                          : t('Forward Call')}
                      </Text>
                      <Text>
                        {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO
                          ? t('All calls are automatically sent to voicemail')
                          : t('All calls are forwarded to a predetermined phone number')}
                      </Text>
                    </div>
                  )}
                  <dl
                    css={{
                      display: 'flex',
                      gap: theme.spacing(2),
                      flexDirection: 'column',
                    }}
                  >
                    {' '}
                    {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO && (
                      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                        <Text weight='bold'>{t('Voicemail Box')}</Text>
                        <Text>{voicemailBox?.[0]?.mailbox?.name}</Text>
                      </div>
                    )}
                    <div css={{ display: 'flex', gap: theme.spacing(3) }}>
                      {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO ? (
                        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                          <Text weight='bold'>{t('Voicemail Greeting')} </Text>
                          {/* {media?.name} */}
                          {/* {voicemailBoxProps.value} */}
                          <Text>{getMediaNameById(vmOverrideMediaProps.value)}</Text>
                        </div>
                      ) : (
                        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                          <Text weight='bold'>{t('Forwarding Number')}</Text>
                          <Text>
                            {forwardingNumber?.name} ({forwardingNumber?.number})
                          </Text>
                        </div>
                      )}
                      {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO &&
                        getMediaNameById(vmOverrideMediaProps.value) !== 'No Greeting' && (
                          <div css={{ width: '250px' }}>
                            <CachedAudioScrubber
                              filePath={vmOverrideMediaProps.value ? resolvedPath : DEFAULT_GREETING_PATH}
                              mediaId={vmOverrideMediaProps.value ? vmOverrideMediaProps.value : 'default'}
                            />
                          </div>
                        )}
                    </div>
                  </dl>
                  {optionalFieldProps.value && (
                    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
                      <Heading level={3}>{t('Start/End Time')}</Heading>
                      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                        <div css={{ display: 'flex', gap: theme.spacing(12.5) }}>
                          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                            <Text weight='bold'>{t('Start Date')} </Text>
                            <Text>
                              {startDateFieldProps.value
                                ? dayjs(startDateFieldProps.value, 'MM/DD/YYYY').format('M/D/YYYY')
                                : t('N/A')}
                            </Text>
                          </div>
                          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                            <Text weight='bold'>{t('Start Time')}</Text>
                            <Text>
                              {startTimeFieldProps.value
                                ? dayjs(startTimeFieldProps.value, 'HH:mm:ss').format('h:mma')
                                : t('N/A')}
                            </Text>
                          </div>
                        </div>
                        <Text color='light' size='small'>
                          {t('Override will automatically start on this date and time')}
                        </Text>
                      </div>

                      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                        <div css={{ display: 'flex', gap: theme.spacing(12.5) }}>
                          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                            <Text weight='bold'>{t('End Date')} </Text>
                            <Text>
                              {endDateFieldProps.value
                                ? dayjs(endDateFieldProps.value, 'MM/DD/YYYY').format('M/D/YYYY')
                                : t('N/A')}
                            </Text>
                          </div>
                          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                            <Text weight='bold'>{t('End Time')}</Text>
                            <Text>
                              {endTimeFieldProps.value
                                ? dayjs(endTimeFieldProps.value, 'HH:mm:ss').format('h:mma')
                                : t('N/A')}
                            </Text>
                          </div>
                        </div>
                        <Text color='light' size='small'>
                          {t('Override will automatically stop on this date and time')}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <Tray.Header
                  Buttons={
                    <Button
                      iconName='x'
                      variant='secondary'
                      size='large'
                      label='close'
                      onClick={() => {
                        modalControls.closeModal();
                        // setTimeout for form to reset after closing the modal
                        setTimeout(() => {
                          handleCancel();
                        }, 300);
                      }}
                    />
                  }
                >
                  <Heading level={2} css={{ marginBottom: 0 }}>
                    {isEdit ? t('Edit Override') : t('Create Override')}
                  </Heading>
                </Tray.Header>
                {override?.callRouteName && !isCallRouteFlow && (
                  <TraySubHeader
                    scheduleCallRoutes={scheduledCallRoute}
                    selectedLocationId={override?.locationId ?? ''}
                    routeType={isDeviceRouteOverride ? 'deviceRoute' : 'callRoute'}
                  />
                )}
                <Text size='medium' css={{ marginBottom: theme.spacing(2) }}>
                  {t(
                    'Choose the override type and, if you wish to plan ahead, select the start and end dates for the override.'
                  )}
                </Text>

                <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
                  {t('Select Override Type')}
                </Heading>
                <div css={overrideSettingsStyle}>
                  <RadioCardField
                    {...overrideSelectProps}
                    css={css`
                      > div:first-of-type {
                        display: flex;
                        flex-wrap: wrap;
                        gap: ${theme.spacing(2)};
                      }
                    `}
                  >
                    {overrideOptions.map((option) => (
                      <RadioCardField.Option
                        key={option.value}
                        value={option.value}
                        trackingId={option.trackingId}
                        css={css`
                          flex: 0 0 240px;
                        `}
                        title={option.title}
                        description={option.description}
                      />
                    ))}
                  </RadioCardField>

                  {/* Dropdown fields for Voicemail override */}
                  {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO && (
                    <div>
                      <DropdownField
                        {...voicemailBoxProps}
                        css={{ maxWidth: theme.spacing(37.5), marginBottom: theme.spacing(3) }}
                        label={t('Select Voicemail Box')}
                        name='voicemailBoxId'
                      >
                        {!!lastUsedVoicemailBoxId && (
                          <DropdownField.OptionGroup label={t('Most Recently Used')}>
                            <DropdownField.Option
                              key={lastUsedVoicemailBoxId}
                              value={lastUsedVoicemailBoxId}
                              css={{ '&:hover': { backgroundColor: theme.colors.neutral5 } }}
                            >
                              {getVoicemailBoxNameById(lastUsedVoicemailBoxId)}
                            </DropdownField.Option>
                          </DropdownField.OptionGroup>
                        )}
                        {
                          <DropdownField.OptionGroup label={t('Voicemail Boxes')}>
                            {filteredVoicemailBoxes?.map((option) => (
                              <DropdownField.Option key={option.mailbox.id} value={option.mailbox.id}>
                                {option.mailbox.name}
                              </DropdownField.Option>
                            ))}
                          </DropdownField.OptionGroup>
                        }
                      </DropdownField>
                      <AudioPicker
                        allowedOptions={{
                          add: true,
                          custom: true,
                          default: true,
                          recent: true,
                          none: true,
                          mailbox: true,
                        }}
                        widths={{ field: 300, scrubber: 300 }}
                        field={vmOverrideMediaProps}
                        name='voicemailMediaId'
                        labels={{ field: t('Select Voicemail Greeting') }}
                        tenantId={phoneTenantId}
                        lastUsedMediaItemId={lastUsedMediaItemId}
                        mailboxId={voicemailBoxProps.value}
                      />
                    </div>
                  )}

                  {/* Dropdown field for Call Forwarding override */}
                  {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_FWD && (
                    <ForwardingNumberPicker
                      {...callForwardingNumberProps}
                      name='forwardingNumberId'
                      label={t('Select Forwarding Number')}
                      locationId={locationId}
                      lastUsedForwardingNumberId={lastUsedForwardingNumberId}
                    />
                  )}

                  {/* Dropdown fields for Phone Tree override */}
                  {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_IVR && (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: ${theme.spacing(3)};
                      `}
                    >
                      <div
                        css={css`
                          .audio-picker {
                            display: flex;
                            flex-direction: column;
                            gap: ${theme.spacing(0)};

                            > div:first-of-type {
                              min-width: 100%;
                            }

                            > div:last-of-type {
                              max-width: 240px;
                            }
                          }
                        `}
                      >
                        <AudioPicker
                          allowedOptions={{
                            add: true,
                            custom: true,
                            default: false,
                            recent: true,
                            none: true,
                            mailbox: false,
                          }}
                          field={phoneTreeInfoMediaIdProps}
                          name='phoneTreeInfoMediaId'
                          labels={{
                            field: t('Select Informational Message (Optional)'),
                            none: t('No Audio File (Default)'),
                          }}
                          tenantId={phoneTenantId}
                          helperText={t(
                            'An informational message plays before the Phone Tree prompt during overrides.'
                          )}
                          lastUsedMediaItemId={lastUsedIVRMediaId}
                        />
                      </div>

                      <div
                        css={css`
                          display: flex;
                          gap: ${theme.spacing(1)};
                          flex-direction: column;
                        `}
                      >
                        <PhoneTreeDropdown
                          dropdownProps={phoneTreeIdProps}
                          name='phoneTreeId'
                          phoneTreesInCallRoute={phoneTreesInCallRoute}
                          currentCallRouteId={callRouteId}
                          prioritizedLocationId={iLID}
                          lastUsedPhoneTreeId={lastUsedIVRId}
                          css={css`
                            width: 100%;
                          `}
                        />
                        <Button
                          variant='tertiary'
                          iconName='view'
                          css={css`
                            width: fit-content;
                          `}
                          {...previewPhoneTreeTriggerProps}
                        >
                          {t('Preview Phone Tree')}
                        </Button>
                      </div>

                      {!!phoneTreeData?.promptMediaItemId && (
                        <PhoneTreePromptSection
                          mediaItemId={phoneTreeData.promptMediaItemId}
                          audioFiles={
                            audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }
                          }
                        />
                      )}

                      <Modal {...previewPhoneTreeModalProps} minWidth={800} maxWidth={800}>
                        <PhoneTreePreviewModal
                          phoneTreeId={phoneTreeIdProps.value ?? ''}
                          name={phoneTreeData?.name ?? ''}
                          onClose={previewPhoneTreeModalProps.onClose}
                          audioFiles={
                            audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }
                          }
                        />
                      </Modal>
                    </div>
                  )}

                  <CheckboxField
                    {...optionalFieldProps}
                    label={
                      <>
                        <Text>{t('Add Start/End Time (Optional)')}</Text>
                        <Text size='medium' color='light'>
                          {t('Schedule when your override starts, ends, or both.')}
                        </Text>
                      </>
                    }
                    data-trackingid='phone-portal-checkBox-addStartEndTime'
                  />

                  {optionalFieldProps.value && (
                    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
                      <div>
                        <div css={{ display: 'flex', gap: theme.spacing(1) }}>
                          <DatePickerField
                            label={t('Start Date')}
                            {...startDateFieldProps}
                            css={{ width: '155px' }}
                            minDate={currentDateFormatted}
                            data-trackingid='phone-portal-dateField-startDate'
                          />
                          <TimeField
                            {...startTimeFieldProps}
                            showAdornment
                            label={t('Start Time')}
                            interval={15}
                            minTime={isStartToday ? currentTimeOffice : '0:00'}
                            maxTime='24:00'
                            css={{ width: '155px' }}
                            value={startTimeFieldProps.value ? startTimeFieldProps.value : ''}
                            data-trackingid='phone-portal-timeField-startTime'
                          />
                        </div>
                        <Text color='light' size='small'>
                          {t('Override will automatically start on this date and time')}
                        </Text>
                      </div>
                      <div>
                        <div css={{ display: 'flex', gap: theme.spacing(1) }}>
                          <DatePickerField
                            label={t('End Date')}
                            {...endDateFieldProps}
                            css={{ width: '155px' }}
                            minDate={startDate ? startDate : currentDateFormatted}
                            data-trackingid='phone-portal-dateField-endDate'
                          />
                          <TimeField
                            {...endTimeFieldProps}
                            showAdornment
                            label={t('End Time')}
                            interval={15}
                            minTime={isEndToday ? currentTimeOffice : '0:00'}
                            maxTime='24:00'
                            css={{ width: '155px' }}
                            value={endTimeFieldProps.value ? endTimeFieldProps.value : ''}
                            data-trackingid='phone-portal-timeField-endTime'
                          />
                        </div>
                        <Text color='light' size='small'>
                          {t('Override will automatically stop on this date and time')}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {hasUserManagementPermission(override?.locationId ?? '') && (
              <section css={newOverrideButtonSectionStyle}>
                <>
                  <div>
                    {isEdit && (
                      <TextLink
                        weight='bold'
                        color='error'
                        css={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing(0.5),
                        }}
                        onClick={() => {
                          handleOpenRemoveModalV2(override);
                        }}
                        size='large'
                      >
                        <Icon name='remove2-small' />
                        {t('Remove Override')}
                      </TextLink>
                    )}
                  </div>
                  <div css={{ display: 'flex', gap: theme.spacing(2) }}>{renderButtons()}</div>
                </>
                <NewOverrideSettingRemoveModal
                  override={overrideV2 ?? ({} as PhoneOverrideType)}
                  modalProps={removeOverride.modalProps}
                  phoneTenantId={phoneTenantId}
                  modalControls={modalControls}
                />
              </section>
            )}
          </div>
        </form>
      </Tray>
    </>
  );
};

const PhoneTreePromptSection = ({ mediaItemId, audioFiles }: { mediaItemId: string; audioFiles: AudioQueryMedia }) => {
  const { t } = useTranslation('phone');

  if (!mediaItemId) {
    return null;
  }

  let filePath = '';
  const media = findMediaItem(mediaItemId, audioFiles?.allMedia || []);
  if (media) {
    filePath = buildAudioLibraryPath({ media });
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(0.5)};
        max-width: 240px;
        position: relative;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: ${theme.spacing(0.5)};
        `}
      >
        <Text size='small' color='subdued'>
          {t('Prompt:')}
        </Text>
        <Truncated as={Text} size='small' color='subdued'>
          {getNameWithoutExtension(media?.name)}
        </Truncated>
      </div>
      <CachedAudioScrubber singlePlayer mediaId={mediaItemId} filePath={filePath} />
    </div>
  );
};
